// Digital Receptionist (IVR) editor — General + Options tabs.
// Wired to the real IVR backend (/api/ivr → v_ivr_menus + v_ivr_menu_options +
// stratus_ext.inbound_rules DID sync, #82). The route shape is
// /digital-receptionist/new and /digital-receptionist/:id/edit. Only General
// is enabled in create-mode (Options needs a saved entity to attach prompts).

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Lock } from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';
import { getScopedDomain } from '../lib/tenantScope';
import AudioPicker from '../components/AudioPicker';
import DidPicker from '../components/DidPicker';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { 'Content-Type': 'application/json', ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const KEYS = ['0','1','2','3','4','5','6','7','8','9','*','#','timeout','invalid'] as const;
type Key = typeof KEYS[number];

const ACTIONS = [
  { v: 'none',         label: 'None' },
  { v: 'extension',    label: 'Extension' },
  { v: 'voicemail',    label: 'Voicemail' },
  { v: 'ring_group',   label: 'Ring Group' },
  { v: 'queue',        label: 'Queue' },
  { v: 'ivr',          label: 'Digital Receptionist' },
  { v: 'external',     label: 'External Number' },
  { v: 'directory',    label: 'Company Directory' },
  { v: 'end_call',     label: 'End Call' },
  { v: 'play_announcement', label: 'Play Announcement' },
  { v: 'repeat',       label: 'Repeat Prompt' },
];

type Menu = Record<Key, { action: string; destination: string }>;
const emptyMenu = (): Menu =>
  KEYS.reduce((m, k) => { m[k] = { action: 'none', destination: '' }; return m; }, {} as Menu);

const LANGS = ['English (US)','English (UK)','English (AU)','Spanish','French','German','Italian','Portuguese (BR)','Dutch','Polish'];

const TYPES = [
  { v: 'standard',  label: 'Standard' },
  { v: 'multi',     label: 'Multi-level' },
  { v: 'tts',       label: 'Text-to-Speech' },
];

export default function DigitalReceptionistEditor() {
  const { id: routeId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const me = api.me();

  const isNew = !routeId && /\/digital-receptionist\/new\/?$/.test(location.pathname);
  const id = routeId || '';
  const [tab, setTab] = useState<'general' | 'options'>('general');

  const detail = useQuery<any>({
    queryKey: ['ivr-settings', id],
    queryFn: () => fJson(`/api/ivr/${id}`),
    enabled: !!id && !isNew,
  });

  const lockedTab = (t: string) => isNew && t !== 'general';
  function pickTab(t: any) {
    if (lockedTab(t)) {
      toast('error', 'Save the Digital Receptionist first — Options needs a saved entity.');
      return;
    }
    setTab(t);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/ring-groups')} className="btn-ghost inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Call Handling
        </button>
        <h1 className="text-xl font-semibold flex-1">
          {isNew ? 'Add Digital Receptionist' :
            `Edit Digital Receptionist${detail.data ? ` — ${detail.data.extension || ''} (${detail.data.name || ''})` : ''}`}
        </h1>
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {([['general','General'],['options','Options']] as const).map(([v, l]) => {
          const locked = lockedTab(v);
          const active = tab === v;
          return (
            <button key={v}
                    onClick={() => pickTab(v)}
                    title={locked ? 'Save the Digital Receptionist first to unlock this tab' : undefined}
                    className={`px-4 py-2 text-sm whitespace-nowrap inline-flex items-center gap-1 ${
                      active ? 'border-b-2 border-primary text-primary'
                      : locked ? 'text-textSecondary/40 cursor-not-allowed'
                      : 'text-textSecondary hover:text-textPrimary'
                    }`}>
              {l}{locked && <Lock size={10} />}
            </button>
          );
        })}
      </div>

      {tab === 'general' && (
        isNew
          // Department defaults to the top-bar "Viewing" scope (falls back to the
          // admin's home dept) so the destination/DID pickers filter to the tenant
          // the operator is actually working on (#108).
          ? <NewGeneralPanel domainUuid={getScopedDomain(me?.domain_uuid) || ''} onCreated={(newId) => navigate(`/digital-receptionist/${newId}/edit`)} />
          : <ExistingGeneralPanel id={id} qc={qc} toast={toast} />
      )}
      {tab === 'options' && !isNew && <OptionsPanel id={id} qc={qc} toast={toast} />}
    </div>
  );
}

// ─────────────────────────────────────── Create-mode General ──────────────────

function NewGeneralPanel({ domainUuid, onCreated }: { domainUuid: string; onCreated: (id: string) => void }) {
  const toast = useToast();
  const [name, setName]           = useState('');
  const [extension, setExtension] = useState('');
  const [type, setType]           = useState('standard');
  const [department, setDepartment] = useState(domainUuid);
  const [language, setLanguage]   = useState('English (US)');
  const [assignedDids, setAssignedDids] = useState<any[]>([]);
  const [destOk, setDestOk]       = useState({ kind: 'extension', value: '' });
  const [menu, setMenu]           = useState<Menu>(emptyMenu());

  const tenantsQ  = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  // Prefill the next free virtual extension (same source the Ring Group editor
  // uses) — 3CX auto-assigns this; the field stays editable and the server
  // rejects duplicates (#115).
  const nextExt = useQuery<any>({ queryKey: ['ch-next-extension'], queryFn: () => fJson('/api/call-handling/next-extension') });
  useEffect(() => {
    const n = nextExt.data && nextExt.data.next;
    if (n) setExtension(e => e || String(n));
  }, [nextExt.data]);

  const create = useMutation({
    mutationFn: () => fJson(`/api/ivr`, {
      method: 'POST',
      body: JSON.stringify({
        domain_uuid: department,
        name, extension, language, type,
        prompt_id: (menu as any).prompt_id || '',
        menu,
        assigned_dids: assignedDids,
        default_destination: destOk,
      }),
    }),
    onSuccess: (data: any) => {
      toast('success', 'Digital Receptionist created');
      onCreated(data.id);
    },
    onError: (e: any) => toast('error', String(e.message || e)),
  });

  return (
    <GeneralForm
      saving={create.isPending}
      onSave={() => {
        if (!name)      return toast('error', 'Name required');
        if (!extension) return toast('error', 'Virtual Extension required');
        if (!department) return toast('error', 'Department required');
        create.mutate();
      }}
      tenants={tenantsQ.data || []}
      values={{ name, extension, type, department, language, assignedDids, destOk, menu }}
      setters={{ setName, setExtension, setType, setDepartment, setLanguage, setAssignedDids, setDestOk, setMenu }}
    />
  );
}

// ─────────────────────────────────────── Edit-mode General ────────────────────


function ExistingGeneralPanel({ id, qc, toast }: { id: string; qc: any; toast: any }) {
  const detail = useQuery<any>({ queryKey: ['ivr-settings', id], queryFn: () => fJson(`/api/ivr/${id}`) });
  const [name, setName]           = useState('');
  const [extension, setExtension] = useState('');
  const [type, setType]           = useState('standard');
  const [department, setDepartment] = useState('');
  const [language, setLanguage]   = useState('English (US)');
  const [assignedDids, setAssignedDids] = useState<any[]>([]);
  const [destOk, setDestOk]       = useState({ kind: 'extension', value: '' });
  const [menu, setMenu]           = useState<Menu>(emptyMenu());

  useEffect(() => {
    const d = detail.data;
    if (!d) return;
    setName(d.name || '');
    setExtension(d.extension || '');
    setType(d.type || 'standard');
    setDepartment(d.domain_uuid || '');
    setLanguage(d.language || 'English (US)');
    setAssignedDids(Array.isArray(d.assigned_dids) ? d.assigned_dids : []);
    setDestOk(d.default_destination || { kind: 'extension', value: '' });
    setMenu({ ...emptyMenu(), ...(d.menu || {}), prompt_id: d.prompt_id || '' } as any);
  }, [detail.data]);

  const tenantsQ = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  const save = useMutation({
    mutationFn: () => fJson(`/api/ivr/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        domain_uuid: department,
        name, extension, language, type,
        prompt_id: (menu as any).prompt_id || '',
        menu,
        assigned_dids: assignedDids,
        default_destination: destOk,
      }),
    }),
    onSuccess: () => {
      toast('success', 'Saved');
      qc.invalidateQueries({ queryKey: ['ivr-settings', id] });
      qc.invalidateQueries({ queryKey: ['did-inventory'] });
    },
    onError: (e: any) => toast('error', String(e.message || e)),
  });

  return (
    <GeneralForm
      saving={save.isPending}
      onSave={() => save.mutate()}
      selfId={id}
      tenants={tenantsQ.data || []}
      values={{ name, extension, type, department, language, assignedDids, destOk, menu }}
      setters={{ setName, setExtension, setType, setDepartment, setLanguage, setAssignedDids, setDestOk, setMenu }}
    />
  );
}

// ─────────────────────────────────────── General form (shared) ────────────────

type Vals = {
  name: string; extension: string; type: string; department: string;
  language: string; assignedDids: any[];
  destOk: { kind: string; value: string };
  menu: Menu;
};
type Setters = {
  setName: (v: string) => void; setExtension: (v: string) => void;
  setType: (v: string) => void; setDepartment: (v: string) => void;
  setLanguage: (v: string) => void; setAssignedDids: (v: any[]) => void;
  setDestOk: (v: { kind: string; value: string }) => void;
  setMenu: (v: Menu) => void;
};

function GeneralForm({
  saving, onSave, tenants, values, setters, selfId,
}: {
  saving: boolean; onSave: () => void;
  tenants: any[];
  values: Vals; setters: Setters; selfId?: string;
}) {
  const { name, extension, type, department, language, assignedDids, destOk, menu } = values;
  const { setName, setExtension, setType, setDepartment, setLanguage, setAssignedDids, setDestOk, setMenu } = setters;

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="btn-primary inline-flex items-center gap-1 text-sm" disabled={saving} onClick={onSave}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="card p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Virtual Extension Number">
            <input className="input" value={extension} onChange={e => setExtension(e.target.value)} placeholder="e.g. 800" />
          </Field>
          <Field label="Assigned DID number(s)">
            <DidPicker value={assignedDids} onChange={setAssignedDids} selfRef={extension} />
          </Field>

          <Field label="Digital Receptionist Name">
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Main Menu" />
          </Field>
          <Field label="Type">
            <select className="input" value={type} onChange={e => setType(e.target.value)}>
              {TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>

          <Field label="Department">
            <select className="input" value={department} onChange={e => setDepartment(e.target.value)}>
              <option value="">— Choose —</option>
              {tenants.map((t: any) => (
                <option key={t.domain_uuid} value={t.domain_uuid}>
                  {t.domain_description || t.domain_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Digital Receptionist language">
            <select className="input" value={language} onChange={e => setLanguage(e.target.value)}>
              {LANGS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        </div>

        {/* Prompt */}
        <div>
          <div className="text-sm font-medium mb-2">Prompt</div>
          <AudioPicker value={(menu as any).prompt_id || ''} onChange={v => setMenu({ ...menu, prompt_id: v } as any)} placeholder="No prompt selected" />
        </div>

        {/* Menu */}
        <div>
          <div className="text-sm font-medium mb-2">Menu</div>
          <div className="card p-0 overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th className="w-24">Key</th>
                  <th>Action</th>
                  <th>Destination</th>
                </tr>
              </thead>
              <tbody>
                {KEYS.map(k => (
                  <tr key={k}>
                    <td className="font-mono">{k}</td>
                    <td>
                      <select className="input" value={menu[k].action}
                              onChange={e => setMenu({ ...menu, [k]: { ...menu[k], action: e.target.value } })}>
                        {ACTIONS.map(a => <option key={a.v} value={a.v}>{a.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <DestValueInput kind={menu[k].action} value={menu[k].destination}
                                      onChange={val => setMenu({ ...menu, [k]: { ...menu[k], destination: val } })}
                                      department={department} selfId={selfId} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Destination if no input / errors out */}
        <div>
          <div className="text-sm font-medium mb-2">Destinations</div>
          <DestinationRow label="Default destination" v={destOk} onChange={setDestOk}
                          department={department} selfId={selfId} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      {children}
    </div>
  );
}

// Destination value input, aware of the action kind + department (#101).
// Ring Group / Extension / Voicemail / Queue / Digital Receptionist render a
// dropdown of the DEPARTMENT's real entities (3CX-style) instead of free text;
// External stays free text; Play Announcement uses the prompt picker; kinds
// with no target (End Call / Directory / Repeat) disable the field.
function DestValueInput({ kind, value, onChange, department, selfId }: {
  kind: string; value: string; onChange: (v: string) => void;
  department?: string; selfId?: string;
}) {
  const listKind =
    kind === 'extension' || kind === 'voicemail' ? 'extensions'
    : kind === 'ring_group' ? 'ring-groups'
    : kind === 'queue' ? 'queues'
    : kind === 'ivr' ? 'ivr'
    : null;
  const listQ = useQuery<any[]>({
    queryKey: ['dest-options', listKind, department],
    queryFn: () => fJson(`/api/tenants/${department}/${listKind}`),
    enabled: !!department && !!listKind,
  });

  if (kind === 'play_announcement') {
    return <AudioPicker value={value} onChange={onChange} placeholder="Choose announcement" />;
  }
  // No department context (e.g. Options tab) — keep the plain text input.
  if (listKind && !department) {
    return <input className="input" value={value} placeholder="extension / number"
                  onChange={e => onChange(e.target.value)} />;
  }
  if (!listKind) {
    const dead = kind === 'none' || kind === 'end_call' || kind === 'directory' || kind === 'repeat' || kind === 'accept_anyway';
    return (
      <input className="input" value={dead ? '' : value}
             placeholder={dead ? '' : 'external number'}
             disabled={dead}
             onChange={e => onChange(e.target.value)} />
    );
  }

  const opts: { v: string; label: string }[] = (listQ.data || [])
    .filter((r: any) => !(kind === 'ivr' && selfId && (r.ivr_menu_uuid === selfId)))
    .map((r: any) => {
      if (listKind === 'extensions')  return { v: String(r.extension), label: `${r.extension} — ${r.effective_caller_id_name || r.description || ''}`.trim() };
      if (listKind === 'ring-groups') return { v: String(r.ring_group_extension), label: `${r.ring_group_name || 'Ring Group'} (${r.ring_group_extension})` };
      if (listKind === 'queues')      return { v: String(r.extension), label: `${r.name || 'Queue'} (${r.extension})` };
      return { v: String(r.ivr_menu_extension), label: `${r.ivr_menu_name || 'Digital Receptionist'} (${r.ivr_menu_extension})` };
    })
    .filter(o => o.v && o.v !== 'null' && o.v !== 'undefined');

  return (
    <select className="input" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">{!department ? '— choose a Department first —' : listQ.isLoading ? 'Loading…' : opts.length ? '— Choose —' : '— none in this department —'}</option>
      {value && !opts.some(o => o.v === String(value)) && <option value={value}>{value}</option>}
      {opts.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}

function DestinationRow({ label, v, onChange, department, selfId }: {
  label: string;
  v: { kind: string; value: string };
  onChange: (v: { kind: string; value: string }) => void;
  department?: string; selfId?: string;
}) {
  const KINDS = [
    { v: 'end_call',    label: 'End Call' },
    { v: 'extension',   label: 'Extension' },
    { v: 'voicemail',   label: 'Voicemail' },
    { v: 'ring_group',  label: 'Ring Group' },
    { v: 'queue',       label: 'Queue' },
    { v: 'ivr',         label: 'Digital Receptionist' },
    { v: 'external',    label: 'External Number' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
      <div className="text-sm text-textSecondary">{label}</div>
      <select className="input" value={v.kind} onChange={e => onChange({ ...v, kind: e.target.value })}>
        {KINDS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
      </select>
      <DestValueInput kind={v.kind} value={v.value} onChange={val => onChange({ ...v, value: val })}
                      department={department} selfId={selfId} />
    </div>
  );
}

// ─────────────────────────────────────── Options ──────────────────────────────

function OptionsPanel({ id, qc, toast }: { id: string; qc: any; toast: any }) {
  const detail = useQuery<any>({ queryKey: ['ivr-options', id], queryFn: () => fJson(`/api/call-routing/ivrs/${id}/settings`) });

  const [forwardSms,         setForwardSms]         = useState({ enabled: false, target: '' });
  const [invalidAction,      setInvalidAction]      = useState<'repeat' | 'destination' | 'end_call'>('repeat');
  const [invalidDest,        setInvalidDest]        = useState({ kind: 'extension', value: '' });
  const [noInputSeconds,     setNoInputSeconds]     = useState(5);
  const [noInputDest,        setNoInputDest]        = useState({ kind: 'extension', value: '' });
  const [allowDial,          setAllowDial]          = useState(true);

  useEffect(() => {
    const d = detail.data;
    if (!d) return;
    setForwardSms(d.forward_sms || { enabled: false, target: '' });
    setInvalidAction(d.invalid_input_action || 'repeat');
    setInvalidDest(d.invalid_input_destination || { kind: 'extension', value: '' });
    setNoInputSeconds(d.no_input_seconds ?? 5);
    setNoInputDest(d.no_input_destination || { kind: 'extension', value: '' });
    setAllowDial(d.allow_dial_extensions ?? true);
  }, [detail.data]);

  const save = useMutation({
    mutationFn: () => fJson(`/api/call-routing/ivrs/${id}/settings`, {
      method: 'PUT',
      body: JSON.stringify({
        forward_sms: forwardSms,
        invalid_input_action: invalidAction,
        invalid_input_destination: invalidDest,
        no_input_seconds: noInputSeconds,
        no_input_destination: noInputDest,
        allow_dial_extensions: allowDial,
      }),
    }),
    onSuccess: () => {
      toast('success', 'Saved');
      qc.invalidateQueries({ queryKey: ['ivr-options', id] });
    },
    onError: (e: any) => toast('error', String(e.message || e)),
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="btn-primary inline-flex items-center gap-1 text-sm" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="card p-4 space-y-4">
        {/* Forward SMS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={forwardSms.enabled}
                   onChange={e => setForwardSms({ ...forwardSms, enabled: e.target.checked })} />
            Forward SMS to
          </label>
          <input className="input md:col-span-2" placeholder="extension or external number"
                 value={forwardSms.target} disabled={!forwardSms.enabled}
                 onChange={e => setForwardSms({ ...forwardSms, target: e.target.value })} />
        </div>

        {/* Invalid input destination */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Invalid input destination</div>
          <div className="flex flex-wrap gap-3 text-sm">
            <label className="inline-flex items-center gap-1">
              <input type="radio" checked={invalidAction === 'repeat'} onChange={() => setInvalidAction('repeat')} />
              Repeat Prompt
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="radio" checked={invalidAction === 'destination'} onChange={() => setInvalidAction('destination')} />
              Send to destination
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="radio" checked={invalidAction === 'end_call'} onChange={() => setInvalidAction('end_call')} />
              End Call
            </label>
          </div>
          {invalidAction === 'destination' && (
            <DestinationRow label="Destination" v={invalidDest} onChange={setInvalidDest} />
          )}
        </div>

        {/* No-input timeout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
          <div className="text-sm">If no input within</div>
          <div className="inline-flex items-center gap-1">
            <input type="number" min={1} className="input w-24" value={noInputSeconds}
                   onChange={e => setNoInputSeconds(Number(e.target.value || 0))} />
            <span className="text-sm text-textSecondary">seconds</span>
          </div>
          <div></div>
          <div className="text-sm md:col-start-1">Send call to</div>
          <div className="md:col-span-2">
            <DestinationRow label="" v={noInputDest} onChange={setNoInputDest} />
          </div>
        </div>

        {/* Allow dial */}
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={allowDial} onChange={e => setAllowDial(e.target.checked)} />
          Allow Callers to Dial Extensions
        </label>
      </div>
    </div>
  );
}
